import { useState, useEffect, useContext, useMemo, useRef } from 'react'
import { Heading, MetricCard } from '../components/common/atoms'
import LazySection from '../components/common/atoms/LazySection'
import { RoundedMultiSelect } from '../components/tailwind/atoms'
import { PeriodPicker } from '../components/common/molecules'
import { handleAppError } from '../components/handlers'
import { AppDispatchContext } from '../utils/contexts'
import { ContractsManager } from '../managers'
import { utilizationApi } from '../services'
import { OverviewApi } from '../services'
import moment from "moment";
import { BENEFITS, formatAmount } from '../constants/overview'
import useReloadOnRelease from '../utils/useReloadOnRelease'
import { BenefitsTrends, CommonSplitPlot, ClaimsDataTable, ClaimsSplit, ClaimsTrends, RelationshipSplit, StatusDataTable } from '../components/opdClaims'
import OpdIcon from '../components/opdClaims/OpdIcon'
import { useHistory } from 'react-router-dom/cjs/react-router-dom'
import { commaingNumber } from '../utils'
import { Tooltip } from 'antd'

const OPDOverview = () => {
  const [state, setState] = useState({ contractDetails: {}, enabledBenefits: {}, openModel: false, benefitSplitData: {}, statusSplitData: {}, serviceStatusSplit: [], relationshipSplitData: {}, topServiceProvider: [], topTrendsBenefits: [], topRejectionReasons: [], topAilmentsList: [], benefitWiseTrends: {}, overallTrends: {}, selectedCompanyIds: [], currentStatus: '', service_types: { status_split: '', benefit_split: '', relationship_split: '', benefit_trends: '' } });
  const [loading, setLoading] = useState({ overviewMetrics: false, benefitSplit: false, statusSplit: false, relationshipSplit: false, topProviders: false, topBenefits: false, topRejection: false, topAilments: false, benefitTrends: false, claimsTrends: false });
  const dispatch = useContext(AppDispatchContext)
  const isOPD = sessionStorage.getItem('isOPDAccount') === 'true';
  const numberFormatter = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
  const history = useHistory()
  const hrFirstName = useMemo(() => {
    const hr_name = sessionStorage.getItem('hr_name')
    if (!hr_name) return ''

    let hrName = hr_name.split(' ')
    return hrName.length > 0 ? hrName[0] : ''
  }, [])

  const setSubState = (newState) => {
    setState(prevState => { return { ...prevState, ...newState } })
  }

  useReloadOnRelease()
  const acceptedCompanyIds = useRef([]);

  useEffect(() => {
    let active = true;
    if (state.selectedCompanyIds.length === 0) {
      fetchContractDetails()
      sessionStorage.removeItem("overviewSelectedCompanies");
    }
    if (state.selectedCompanyIds.length > 1) {
      sessionStorage.setItem("overviewSelectedCompanies", JSON.stringify(state.selectedCompanyIds));
      if (state.contractDetails?.selectedContract) {
        fetchOverviewActivationMetric(state.contractDetails.selectedContract.contract_id)
        fetchOpdOverviewMetric(state.contractDetails.selectedContract.contract_id)
      }
    } if (state.selectedCompanyIds.length === 1) {
      sessionStorage.setItem("overviewSelectedCompanies", JSON.stringify(state.selectedCompanyIds));
    }

    const selected = state.selectedCompanyIds;
    window.dispatchEvent(new CustomEvent("overviewCompaniesChanged", { detail: { onComplete: committed => {
      if (!active) return;
      if (committed) acceptedCompanyIds.current = selected;
      else setSubState({ selectedCompanyIds: acceptedCompanyIds.current });
    } } }));
    if (selected.length !== 1) acceptedCompanyIds.current = selected;
    return () => { active = false; };
  }, [state.selectedCompanyIds])

  useEffect(() => {
    const contract = state.contractDetails.selectedContract;
    if (!contract || state.currentStatus === '') return;

    const { contract_id, start_date, end_date } = contract;
    fetchOpdOverviewMetric(contract_id, start_date, end_date);
  }, [state.currentStatus, state.contractDetails.selectedContract]);

  const prevServiceTypes = useRef(state.service_types);
  useEffect(() => {
    const contract = state.contractDetails.selectedContract;
    if (!contract) return;

    const { contract_id, start_date, end_date } = contract;
    const fetchMap = {
      status_split: fetchOpdStatusWiseSplit,
      benefit_split: fetchOpdBenefitWiseSplit,
      relationship_split: fetchOpdRelationshipWiseSplit,
      benefit_trends: fetchOpdBenefitWiseTrends,
    };

    Object.entries(state.service_types).forEach(([key, value]) => {
      if (value !== prevServiceTypes.current[key] && fetchMap[key]) {
        fetchMap[key](contract_id, start_date, end_date);
      }
    });

    prevServiceTypes.current = state.service_types;
  }, [state.service_types, state.contractDetails.selectedContract]);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem("overviewSelectedCompanies");
      window.dispatchEvent(new Event("overviewCompaniesChanged"));
    };
  }, []);


  const fetchContractDetails = async () => {
    setLoading(prev => ({
      ...prev,
      overviewMetrics: true,
      benefitSplit: true,
      statusSplit: true,
      relationshipSplit: true,
      topProviders: true,
      topBenefits: true,
      topRejection: true,
      topAilments: true,
      benefitTrends: true,
      claimsTrends: true
    }));

    await utilizationApi.fetchContracts({},
      (resp) => {
        var contractObj = new ContractsManager(resp.data)
        const contract_id = Number(sessionStorage.getItem('contract_id'))
        const selectedContract = contractObj.formattedData.find(item => item.contract_id === contract_id) || contractObj.formattedData[0]
        const showSpendingAccount = selectedContract.showSpendingOrRemoteWallet
        const contractDetails = {
          contracts: contractObj.formattedData,
          selectedContract
        }
        setSubState({ contractDetails })
        dispatch({ type: '', showSpendingAccount })
        if (!showSpendingAccount) {
          sessionStorage.setItem('isOPDAccount', showSpendingAccount);
          history.replace('/overview')
          return
        }
        fetchOverviewInitData(selectedContract.contract_id, selectedContract.start_date, selectedContract.end_date, contractDetails)
      },
      (err) => handleAppError(err)
    )
  }

  const fetchOverviewInitData = async (contractId, startDate, endDate, contractDetails) => {
    contractDetails = contractDetails ?? state.contractDetails
    const contract_id = contractId ?? contractDetails.selectedContract.contract_id
    const start_date = startDate ?? contractDetails.selectedContract.start_date
    const end_date = endDate ?? contractDetails.selectedContract.end_date

    await OverviewApi.fetchOverviewInitData({ contract_id, start_date, end_date },
      (res) => {
        const enabledBenefits = res.data.enabled_benefits ?? []

        let sponsoredBenefits = enabledBenefits.filter(item => item.primary === 'primary_sponsored')
        let nonSponsoredBenefits = enabledBenefits.filter(item => item.primary !== 'primary_sponsored')

        let notSponsoredConsultation = nonSponsoredBenefits.map(service => service.benefit_type).includes('consultation24x7')
        sponsoredBenefits = sponsoredBenefits.map(service => service.benefit_type === 'consultation24x7' ? 'consultations' : service.benefit_type).filter(e => BENEFITS.includes(e)) // filtering benefits that are to display
        sponsoredBenefits = ['hra', ...new Set(sponsoredBenefits.includes('pre_employment') ? ['pre_employment', ...sponsoredBenefits] : sponsoredBenefits)]

        nonSponsoredBenefits = nonSponsoredBenefits.map(service => service.benefit_type).filter(e => BENEFITS.includes(e)) // filtering benefits that are to display
        nonSponsoredBenefits = [...new Set(nonSponsoredBenefits.includes('pre_employment') ? ['pre_employment', ...nonSponsoredBenefits] : nonSponsoredBenefits)]

        if (state?.contractDetails?.selectedContract?.contract_id !== contract_id) {
          contractDetails = {
            ...contractDetails,
            selectedContract: contractDetails?.contracts?.find(item => item.contract_id === contract_id)
          }
          setSubState({ enabledBenefits: { sponsoredBenefits, nonSponsoredBenefits }, notSponsoredConsultation: notSponsoredConsultation, contractDetails })
        } else setSubState({ enabledBenefits: { sponsoredBenefits, nonSponsoredBenefits }, notSponsoredConsultation: notSponsoredConsultation })

        callEagerApis(contract_id, start_date, end_date)
      },
      (err) => handleAppError(err)
    )
  }

  const callEagerApis = (contractId, startDate, endDate) => {
    fetchOverviewActivationMetric(contractId, startDate, endDate)
    fetchOpdOverviewMetric(contractId, startDate, endDate)
  }

  const apiParams = (contractId, startDate, endDate) => {
    const contract_id = contractId ?? null
    const start_date = startDate ?? null
    const end_date = endDate ?? null
    const company_ids = state.selectedCompanyIds ?? []
    return { contract_id, start_date, end_date, company_ids }
  }

  const fetchOverviewActivationMetric = async (contractId, startDate, endDate) => {

    await OverviewApi.fetchOverviewMetric({ ...apiParams(contractId, startDate, endDate) },
      (res) => {
        setSubState({ ...res.data })
      },
      (err) => handleAppError(err)
    )
  }

  const fetchOpdOverviewMetric = async (contractId, startDate, endDate) => {
    setLoading(prev => ({ ...prev, overviewMetrics: true }));
    await OverviewApi.fetchOpdOverviewMetric({ ...apiParams(contractId, startDate, endDate), ...{ status: state.currentStatus } },
      (res) => {
        setSubState({ ...res.data })
        setLoading(prev => ({ ...prev, overviewMetrics: false }));
      },
      (err) => {
        handleAppError(err)
        setLoading(prev => ({ ...prev, overviewMetrics: false }));
      }
    )
  }

  const fetchOpdBenefitWiseSplit = async (contractId, startDate, endDate) => {
    setLoading(prev => ({ ...prev, benefitSplit: true }));
    await OverviewApi.fetchOpdBenefitWiseSplit({ ...apiParams(contractId, startDate, endDate), service_type: state.service_types.benefit_split },
      (res) => {
        let data = formatBenefitResponse(res.data)
        setSubState({ ...data })
        setLoading(prev => ({ ...prev, benefitSplit: false }));
      },
      (err) => {
        handleAppError(err)
        setLoading(prev => ({ ...prev, benefitSplit: false }));
      }
    )
  }

  const formatBenefitResponse = (res) => {
    const benefits = res?.benefit_wise_split || {};

    const formatted = {};
    Object.values(benefits).forEach((val) => {
      const benefit_name = val.benefit_name;
      formatted[benefit_name] = {
        volume: val.total_count,
        amount: val.total_amount,
      };
    });

    return { benefitSplitData: formatted };
  };

  const fetchOpdStatusWiseSplit = async (contractId, startDate, endDate) => {
    setLoading(prev => ({ ...prev, statusSplit: true }));
    await OverviewApi.fetchOpdStatusWiseSplit({ ...apiParams(contractId, startDate, endDate), service_type: state.service_types.status_split },
      (res) => {
        let data = formatStatusResponse(res.data)
        let result = res.data?.service_status_split || []
        setSubState({ ...data, serviceStatusSplit: result })
        setLoading(prev => ({ ...prev, statusSplit: false }));
      },
      (err) => {
        handleAppError(err)
        setLoading(prev => ({ ...prev, statusSplit: false }));
      }
    )
  }

  const formatStatusResponse = (res) => {
    const statuses = res?.service_status_split || {};

    const formatted = {};
    Object.values(statuses).forEach((val) => {
      const status_name = val.bucket_status;
      formatted[status_name] = {
        volume: val.total_count,
        amount: val.total_amount,
      };
    });

    return { statusSplitData: formatted };
  };

  const fetchOpdRelationshipWiseSplit = async (contractId, startDate, endDate) => {
    setLoading(prev => ({ ...prev, relationshipSplit: true }));
    await OverviewApi.fetchOpdRelationshipWiseSplit({ ...apiParams(contractId, startDate, endDate), service_type: state.service_types.relationship_split },
      (resp) => {
        let formattedRes = transformRelationshipData(resp.data)
        setSubState({ ...{ relationshipSplitData: formattedRes } })
        setLoading(prev => ({ ...prev, relationshipSplit: false }));
      },
      (err) => {
        handleAppError(err)
        setLoading(prev => ({ ...prev, relationshipSplit: false }));
      }
    )
  }

  const transformRelationshipData = (res) => {
    const result = {};

    (res.relationship_summary || []).forEach(item => {
      const key = item.relation || "Self";

      if (!result[key]) {
        result[key] = { volume: 0, amount: 0 };
      }

      result[key].volume += Number(item.claim_count) || 0;
      result[key].amount += Number(item.total_amount) || 0;
    });

    return result;
  };

  const fetchOpdTopServiceProviders = async (contractId, startDate, endDate) => {
    setLoading(prev => ({ ...prev, topProviders: true }));
    await OverviewApi.fetchOpdTopServiceProviders(apiParams(contractId, startDate, endDate),
      (resp) => {
        const formattedData = transformTopItemsResponse(resp.data, 'Hospital Name', 'provider_name');
        setSubState({ ...{ topServiceProvider: formattedData } })
        setLoading(prev => ({ ...prev, topProviders: false }));
      },
      (err) => {
        handleAppError(err)
        setLoading(prev => ({ ...prev, topProviders: false }));
      }
    )
  }

  const transformTopItemsResponse = (res = [], displayKey, keyName) => {
    return res.map(item => {
      let name = (item[keyName] || "Unknown").replace(/[{}"\\]/g, '').trim();

      const cashlessCount = parseFloat(item.cashless_claim_count) || 0;
      const cashlessValue = Number(item.cashless_amount) || 0;

      const reimbursementCount = parseFloat(item.reimbursement_claim_count) || 0;
      const reimbursementValue = Number(item.reimbursement_amount) || 0;

      const totalValue = cashlessValue + reimbursementValue;

      return {
        [displayKey]: name,
        "Cashless Count": cashlessCount,
        "Cashless Value": `₹${cashlessValue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`,
        "Reimbursement Count": reimbursementCount,
        "Reimbursement Value": `₹${reimbursementValue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`,
        "Total Value": `₹${totalValue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`,
        "Total Count": cashlessCount + reimbursementCount
      };
    });
  };

  const fetchOpdTopBenefits = async (contractId, startDate, endDate) => {
    setLoading(prev => ({ ...prev, topBenefits: true }));
    await OverviewApi.fetchOpdTopBenefits(apiParams(contractId, startDate, endDate),
      (resp) => {
        let formattedData = transformTopItemsResponse(resp.data, 'Benefit Name', 'benefit_name');
        setSubState({ ...{ topTrendsBenefits: formattedData } })
        setLoading(prev => ({ ...prev, topBenefits: false }));
      },
      (err) => {
        handleAppError(err)
        setLoading(prev => ({ ...prev, topBenefits: false }));
      }
    )
  }

  const fetchOpdTopRejectionReasons = async (contractId, startDate, endDate) => {
    setLoading(prev => ({ ...prev, topRejection: true }));
    await OverviewApi.fetchOpdTopRejectionReasons(apiParams(contractId, startDate, endDate),
      (resp) => {
        let formattedData = transformTopItemsResponse(resp.data, 'Rejection Reason', 'rejection_tag');
        setSubState({ ...{ topRejectionReasons: formattedData } })
        setLoading(prev => ({ ...prev, topRejection: false }));
      },
      (err) => {
        handleAppError(err)
        setLoading(prev => ({ ...prev, topRejection: false }));
      }
    )
  }

  const fetchOpdTopAilments = async (contractId, startDate, endDate) => {
    setLoading(prev => ({ ...prev, topAilments: true }));
    await OverviewApi.fetchOpdTopAilments(apiParams(contractId, startDate, endDate),
      (resp) => {
        let formattedData = transformTopItemsResponse(resp.data, 'Ailment', 'ailment_name');
        setSubState({ ...{ topAilmentsList: formattedData } })
        setLoading(prev => ({ ...prev, topAilments: false }));
      },
      (err) => {
        handleAppError(err)
        setLoading(prev => ({ ...prev, topAilments: false }));
      }
    )
  }

  const fetchOpdBenefitWiseTrends = async (contractId, startDate, endDate) => {
    setLoading(prev => ({ ...prev, benefitTrends: true }));
    await OverviewApi.fetchOpdBenefitWiseTrends({ ...apiParams(contractId, startDate, endDate), service_type: state.service_types.benefit_trends },
      (res) => {
        let data = formatBenefitTrends(res.data?.benefit_wise_trends)
        setSubState({ benefitWiseTrends: data })
        setLoading(prev => ({ ...prev, benefitTrends: false }));
      },
      (err) => {
        handleAppError(err)
        setLoading(prev => ({ ...prev, benefitTrends: false }));
      }
    )
  };

  const formatBenefitTrends = (apiResponse) => {
    const parseTrends = (trends, isWeekly = true) => {
      const result = {};

      trends.forEach(({ benefit_name, claim_count, total_amount, week_start, month_name }) => {
        const label = isWeekly ? week_start : month_name;
        if (!result[benefit_name]) {
          result[benefit_name] = { volume: {}, amount: {}, labels: [] };
        }

        result[benefit_name].volume[label] = parseFloat(claim_count) || 0;
        result[benefit_name].amount[label] = parseFloat(total_amount) || 0;

        if (!result[benefit_name].labels.includes(label)) result[benefit_name].labels.push(label);
      });

      // Sort labels chronologically and reorder volume & amount
      Object.keys(result).forEach((benefit) => {
        result[benefit].labels.sort((a, b) => {
          let dateA, dateB;

          if (isWeekly) {
            // Weekly trend: '07-Jul'
            dateA = moment(a, "DD-MMM").toDate();
            dateB = moment(b, "DD-MMM").toDate();
          } else {
            // Month trend: 'Oct-23' or 'Sep-23' => prepend day '01-'
            dateA = moment(`01-${a}`, "DD-MMM").toDate();
            dateB = moment(`01-${b}`, "DD-MMM").toDate();
          }

          return dateA - dateB;
        });

        const orderedVolume = {};
        const orderedAmount = {};
        result[benefit].labels.forEach((label) => {
          orderedVolume[label] = result[benefit].volume[label];
          orderedAmount[label] = result[benefit].amount[label];
        });

        result[benefit].volume = orderedVolume;
        result[benefit].amount = orderedAmount;
      });

      return result;
    };

    return {
      week_on_week: parseTrends(apiResponse?.weekly_trends || [], true),
      month_on_month: parseTrends(apiResponse?.monthly_trends || [], false),
    };
  };

  const fetchOpdOverAllTrends = async (contractId, startDate, endDate) => {
    setLoading(prev => ({ ...prev, claimsTrends: true }));
    await OverviewApi.fetchOpdOverAllTrends(apiParams(contractId, startDate, endDate),
      (res) => {
        let data = transformTrends(res.data)
        setSubState({ overallTrends: data })
        setLoading(prev => ({ ...prev, claimsTrends: false }));
      },
      (err) => {
        handleAppError(err)
        setLoading(prev => ({ ...prev, claimsTrends: false }));
      }
    )
  };

  const transformTrends = (apiResponse) => {
    const build = (period) => {
      const volume = { reimbursement: {}, cashless: {}, total: {} };
      const amount = { reimbursement: {}, cashless: {}, total: {} };
      const allKeys = new Set();

      ["reimbursement_trends", "cashless_trends"].forEach((key) => {
        const benefit = key.replace("_trends", "");
        const data = apiResponse[key]?.[period] || [];

        data.forEach((entry) => {
          let label;
          if (period === "weekly") {
            label = entry.week_start; // e.g., "07-Jul"
          } else {
            label = entry.month_name; // e.g., "Sep-25"
          }

          const count = parseFloat(entry.claim_count) || 0;
          const amt = Number(entry.total_amount) || 0;

          allKeys.add(label);

          volume[benefit][label] = (volume[benefit][label] || 0) + count;
          volume.total[label] = (volume.total[label] || 0) + count;

          amount[benefit][label] = (amount[benefit][label] || 0) + amt;
          amount.total[label] = (amount.total[label] || 0) + amt;
        });
      });

      let orderedLabels;

      if (period === "weekly") {
        // Sort by actual week_start (DD-MMM)
        orderedLabels = [...allKeys].sort((a, b) => {
          const da = moment(a, "DD-MMM").toDate();
          const db = moment(b, "DD-MMM").toDate();
          return da - db;
        });
      } else {
        // Sort by month-year (e.g., May-25 < Jun-25 < Jul-25)
        orderedLabels = [...allKeys].sort((a, b) => {
          const da = moment(a, "MMM-YY").toDate();
          const db = moment(b, "MMM-YY").toDate();
          return da - db;
        });
      }

      // Rebuild ordered objects
      ["reimbursement", "cashless", "total"].forEach((k) => {
        const orderedVolume = {};
        const orderedAmount = {};
        orderedLabels.forEach((label) => {
          orderedVolume[label] = volume[k][label] || 0;
          orderedAmount[label] = amount[k][label] || 0;
        });
        volume[k] = orderedVolume;
        amount[k] = orderedAmount;
      });

      return { volume, amount };
    };

    return {
      week_on_week: build("weekly"),
      month_on_month: build("monthly"),
    };
  };

  const changeContractTo = (contractId) => {
    const contract = state.contractDetails.contracts.find((item) => item.contract_id == contractId)
    sessionStorage.setItem('contract_id', contract.contract_id);
    const showSpendingAccount = contract.showSpendingOrRemoteWallet
    if (!showSpendingAccount) {
      sessionStorage.setItem('isOPDAccount', showSpendingAccount);
      history.replace('/overview')
      return
    }
    fetchOverviewInitData(contract.contract_id, contract.start_date, contract.end_date)
  }

  const toggleModal = () => setSubState({ openModel: !state.openModel })

  const multiSelectCompanyHandler = (value) => {
    setSubState({ selectedCompanyIds: value })
  }

  const showCompanyFilter = () => {
    let companies = JSON.parse(sessionStorage.getItem("companies") || "[]");
    companies = companies.filter(c => c.enable_spending_account || c.remote_wallet_enabled);
    let selectedCompany = JSON.parse(sessionStorage.getItem('company_id')) || null;
    const hasMultipleCompanies = companies.length > 1;

    if (hasMultipleCompanies && isOPD) {
      const selectedIds = state.selectedCompanyIds && state.selectedCompanyIds.length > 0
        ? state.selectedCompanyIds
        : [selectedCompany];

      return (
        <span style={{ paddingLeft: 12 }}>
          <RoundedMultiSelect
            selectValues={selectedIds}
            selectOptions={companies.map(c => ({ label: c.name, value: c.id }))}
            selectHandler={multiSelectCompanyHandler}
            placeholder="Select companies"
          />
        </span>
      );
    } else return null;
  };

  const handleStatusChange = (status) => {
    setSubState({ currentStatus: status })
  }

  const handleServiceChange = (key, value) => {
    let service_types = { ...state.service_types }
    service_types[key] = value
    setSubState({ service_types })
  }

  const { contractDetails: { contracts = [], selectedContract = {} }, active_customers_count = 0, onboarded_customers_count = 0 } = state
  const { contract_id: selectedContractId, start_date: selectedContractStartDate, end_date: selectedContractEndDate, is_running_contract: isRunningContract, uuid: selectedContractUuid } = selectedContract

  const cashlessAmount = Number(state?.cashless_claim_amount) || 0
  const reimbursementAmount = state?.reimbursement_claim_amount || 0
  const cashlessIncurredAmount = state?.cashless_incurred_amount || 0
  const reimbursementIncurredAmount = state?.reimbursement_incurred_amount || 0
  const totalClaimRaisedAmount = state?.total_claim_amount || 0
  const totalClaimsAmount = cashlessAmount + reimbursementAmount
  const totalIncurredAmount = cashlessIncurredAmount + reimbursementIncurredAmount

  const cashlessCount = state?.cashless_claims_count || 0
  const reimbursementCount = state?.reimbursement_claims_count || 0
  const totalClaimRaisedCount = state?.total_claims_count || 0
  const totalUniqueUsers = state?.total_unique_user_count || 0
  const totalUniqueStatusUsersCount = state?.total_unique_status_user_count || 0
  const totalActiveUserCount = state?.total_active_user_count || 0
  const settledCount = state?.settled_claims_count || 0
  const utilizationPercentage = totalActiveUserCount > 0 ? (totalUniqueUsers / totalActiveUserCount) * 100 : 0
  const activationPercentage = active_customers_count > 0 ? (onboarded_customers_count / active_customers_count) * 100 : 0

  const totalClaimsCount = cashlessCount + reimbursementCount

  const ClaimsSplitData = {
    reimbursement: { volume: reimbursementCount, amount: reimbursementAmount },
    cashless: { volume: cashlessCount, amount: cashlessAmount },
    totalClaimAmount: { volume: totalUniqueStatusUsersCount, amount: totalClaimsAmount }
  };

  const baseColumns = ["Total Value", "Total Count", "Cashless Value", "Cashless Count", "Reimbursement Value", "Reimbursement Count"];

  const hospitalColumns = ["Hospital Name", ...baseColumns];
  const benefitColumns = ["Benefit Name", ...baseColumns];
  const rejectionColumns = ["Rejection Reason", ...baseColumns];
  const ailmentColumns = ["Ailment", ...baseColumns];
  const statusColumns = ["Status", "Total Value", "Total Count"];
  const servicesMap = { 'overall': 'Overall', 'cashless': 'Cashless', 'reimbursement': 'Reimbursement' };
  const metricCardClasses = 'min-h-[178px] rounded-lg border-gray-300 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-400 hover:shadow-md';
  const metricTitleClasses = 'text-sm font-semibold text-gray-900';
  const metricSubtitleClasses = 'mt-1 text-xs leading-relaxed text-gray-600';

  return (
    <div className='mb-20 px-1 text-gray-700'>
      <div className='mt-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
        <Heading
          mainHeading='OPD Overview'
          mainClasses='font-fig-extrabold text-4xl leading-tight text-gray-900'
          subHeading={`Hi ${state.selectedCompanyIds.length > 0 ? '' : hrFirstName}, this view shows utilization of wallet-based benefits and does not include usage of Annual Health Checks or PPU services`}
          subClasses='mt-2 max-w-xl text-sm leading-relaxed text-gray-700'
        />
        {/* Bottom-aligned, not centred: the period control carries a label above it and the company
            filter does not, so centring lined up two different total heights and left the two boxes
            visibly off. Aligning their bottoms puts the controls themselves on one line. */}
        <div className='flex flex-wrap items-end justify-end gap-3'>
          <span>{showCompanyFilter()}</span>
          <PeriodPicker
            classes='shrink-0'
            // Without this the control renders nothing on first paint and then pops in, shifting the
            // header — `contracts` is empty until the fetch resolves, and `loading` defaults to false.
            loading={!contracts.length}
            list={contracts}
            currentPage={contracts.find(i => i.contract_id === selectedContractId)?.text}
            setContract={(item) => changeContractTo(item.contract_id)}
            // Across several companies there is no single contract to name, so the control says so and locks.
            valueLabel={state.selectedCompanyIds.length > 1 ? 'Current contract' : undefined}
            disabled={state.selectedCompanyIds.length > 1}
          />
        </div>
      </div>
      <div className='mt-7 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4'>
        <MetricCard
          title='Activation'
          titleClasses={metricTitleClasses}
          subTitle='% of employees who logged in at least once on the ekincare app'
          subTitleClasses={metricSubtitleClasses}
          metricValue={<span className='font-jetbrains-mono text-3xl font-semibold tabular-nums text-gray-900'>{Math.round(activationPercentage)}%</span>}
          metricValueSuffix={<span><span className='font-jetbrains-mono text-sm tabular-nums text-gray-700'>{commaingNumber(onboarded_customers_count)}</span> / <span className='font-jetbrains-mono text-sm tabular-nums text-gray-700'>{commaingNumber(active_customers_count)}</span> employees</span>}
          metricClasses='mt-5 flex flex-col items-start gap-2 text-gray-700'
          icon={<OpdIcon name='activation' tone='brand' size='metric' />}
          loading={loading.overviewMetrics}
          loaderHeight={178}
          classes={metricCardClasses}
        />
        <MetricCard
          title='Engagement'
          titleClasses={metricTitleClasses}
          subTitle='% of unique claimants / employee base'
          subTitleClasses={metricSubtitleClasses}
          metricValue={<span className='font-jetbrains-mono text-3xl font-semibold tabular-nums text-gray-900'>{Math.round(utilizationPercentage)}%</span>}
          metricValueSuffix={<span><span className='font-jetbrains-mono text-sm tabular-nums text-gray-700'>{commaingNumber(totalUniqueUsers)}</span> / <span className='font-jetbrains-mono text-sm tabular-nums text-gray-700'>{commaingNumber(totalActiveUserCount)}</span> employees</span>}
          metricClasses='mt-5 flex flex-col items-start gap-2 text-gray-700'
          icon={<OpdIcon name='engagement' tone='success' size='metric' />}
          loading={loading.overviewMetrics}
          loaderHeight={178}
          classes={metricCardClasses}
        />
        <MetricCard
          title='Claims Filed'
          titleClasses={metricTitleClasses}
          subTitle='Total claims raised by employees'
          subTitleClasses={metricSubtitleClasses}
          metricValue={<span className='font-jetbrains-mono text-3xl font-semibold tabular-nums text-gray-900'>{numberFormatter.format(Math.round(totalClaimRaisedCount))}</span>}
          metricValueSuffix={<span><span className='font-jetbrains-mono text-sm tabular-nums text-gray-700'>{numberFormatter.format(Math.round(settledCount))}</span> settled claims</span>}
          metricClasses='mt-5 flex flex-col items-start gap-2 text-gray-700'
          icon={<OpdIcon name='claims' tone='warning' size='metric' />}
          loading={loading.overviewMetrics}
          loaderHeight={178}
          classes={metricCardClasses}
        />
        <MetricCard
          title='Claims Value'
          titleClasses={metricTitleClasses}
          subTitle='Total value of claims raised by employees'
          subTitleClasses={metricSubtitleClasses}
          metricValue={<Tooltip title={`₹${numberFormatter.format(Math.round(totalClaimRaisedAmount))}`}><span className='font-jetbrains-mono text-3xl font-semibold tabular-nums text-gray-900'>₹{formatAmount(totalClaimRaisedAmount)}</span></Tooltip>}
          metricValueSuffix={<span>Settled amount: <Tooltip title={`₹${numberFormatter.format(Math.round(totalIncurredAmount))}`}><span className='font-jetbrains-mono text-sm tabular-nums text-gray-700'>₹{formatAmount(totalIncurredAmount)}</span></Tooltip></span>}
          metricClasses='mt-5 flex flex-col items-start gap-2 text-gray-700'
          icon={<OpdIcon name='value' tone='accent' size='metric' />}
          loading={loading.overviewMetrics}
          loaderHeight={178}
          classes={metricCardClasses}
        />
      </div>
      <div className='mt-6'>
        <LazySection deps={[selectedContractId, state.selectedCompanyIds]} onVisible={() => selectedContractId && fetchOpdStatusWiseSplit(selectedContractId, selectedContractStartDate, selectedContractEndDate)}>
          <StatusDataTable title="Status Split" columns={statusColumns} rows={state.serviceStatusSplit} loading={loading.statusSplit} servicesMap={servicesMap} currentService={state.service_types.status_split} handleServiceChange={handleServiceChange} />
        </LazySection>
      </div>
      <div className='my-4'>
        <ClaimsSplit data={ClaimsSplitData} loading={loading.overviewMetrics} handleStatusChange={handleStatusChange} currentStatus={state.currentStatus} />
      </div>
      <div className='my-6'>
        <LazySection deps={[selectedContractId, state.selectedCompanyIds]} onVisible={() => selectedContractId && fetchOpdBenefitWiseSplit(selectedContractId, selectedContractStartDate, selectedContractEndDate)}>
          <CommonSplitPlot data={state?.benefitSplitData} title='Benefit-wise Split' loading={loading.benefitSplit} servicesMap={servicesMap} currentService={state.service_types.benefit_split} handleServiceChange={handleServiceChange} showDownload />
        </LazySection>
      </div>
      <div className='my-6'>
        <LazySection deps={[selectedContractId, state.selectedCompanyIds]} onVisible={() => selectedContractId && fetchOpdRelationshipWiseSplit(selectedContractId, selectedContractStartDate, selectedContractEndDate)}>
          <RelationshipSplit data={state?.relationshipSplitData} loading={loading.relationshipSplit} servicesMap={servicesMap} currentService={state.service_types.relationship_split} handleServiceChange={handleServiceChange} />
        </LazySection>
      </div>
      <div className='mt-6'>
        <LazySection deps={[selectedContractId, state.selectedCompanyIds]} onVisible={() => selectedContractId && fetchOpdOverAllTrends(selectedContractId, selectedContractStartDate, selectedContractEndDate)}>
          <ClaimsTrends data={state.overallTrends} loading={loading.claimsTrends} selectedTrend="Month-on-Month" />
        </LazySection>
      </div>
      <div className="my-6">
        <LazySection deps={[selectedContractId, state.selectedCompanyIds]} onVisible={() => selectedContractId && fetchOpdBenefitWiseTrends(selectedContractId, selectedContractStartDate, selectedContractEndDate)}>
          <BenefitsTrends data={state.benefitWiseTrends} loading={loading.benefitTrends} selectedTrend="Month-on-Month" servicesMap={servicesMap} currentService={state.service_types.benefit_trends} handleServiceChange={handleServiceChange} />
        </LazySection>
      </div>
      <div className='mt-6'>
        <LazySection deps={[selectedContractId, state.selectedCompanyIds]} onVisible={() => selectedContractId && fetchOpdTopServiceProviders(selectedContractId, selectedContractStartDate, selectedContractEndDate)}>
          <ClaimsDataTable title="Top 5 Providers by utilisation" columns={hospitalColumns} rows={state.topServiceProvider} loading={loading.topProviders} />
        </LazySection>
      </div>
      <div className='mt-6'>
        <LazySection deps={[selectedContractId, state.selectedCompanyIds]} onVisible={() => selectedContractId && fetchOpdTopBenefits(selectedContractId, selectedContractStartDate, selectedContractEndDate)}>
          <ClaimsDataTable title="Top 5 Benefits by utilisation" columns={benefitColumns} rows={state.topTrendsBenefits} loading={loading.topBenefits} />
        </LazySection>
      </div>
      <div className='mt-6'>
        <LazySection deps={[selectedContractId, state.selectedCompanyIds]} onVisible={() => selectedContractId && fetchOpdTopRejectionReasons(selectedContractId, selectedContractStartDate, selectedContractEndDate)}>
          <ClaimsDataTable title="Top 5 Rejection Reasons" columns={rejectionColumns} rows={state.topRejectionReasons} loading={loading.topRejection} />
        </LazySection>
      </div>
      <div className='mt-6'>
        <LazySection deps={[selectedContractId, state.selectedCompanyIds]} onVisible={() => selectedContractId && fetchOpdTopAilments(selectedContractId, selectedContractStartDate, selectedContractEndDate)}>
          <ClaimsDataTable title="Top 5 Ailments" columns={ailmentColumns} rows={state.topAilmentsList} loading={loading.topAilments} />
        </LazySection>
      </div>
    </div>
  )
}

export default OPDOverview
